import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Puzzle } from "lucide-react";
import { SHAPE_MAP } from "../lib/sewingShapes";
import { COMPOSITES } from "../lib/sewingCurriculum";
import { labHref } from "../lib/sewingLinks";
import SewingShell from "../components/sewing/SewingShell";
import AssemblyLab from "../components/sewing/AssemblyLab";

/* ============================================================
   /sewing-primitives/make — the composition room: how parts
   attach (the assembly lab), what to make first (the starter
   menu), and the creature generator for making something no
   one has ever made before.
   ============================================================ */

const SewingMake = () => (
  <SewingShell
    active="make"
    title="Make"
    intro={
      <p className="mt-4 max-w-2xl text-muted-foreground">
        Composition is the graduation move: decompose an object into
        primitives, sew the primitives, attach them. Here's how attaching
        works, seven objects to start on, and a generator for inventing
        your own.
      </p>
    }
  >
    <div className="mt-10">
      <AssemblyLab />
    </div>

    {/* ---- composites ---- */}
    <section className="mt-16">
      <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
        <Puzzle className="h-6 w-6 text-primary" />
        The starter menu
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Real objects, decomposed into lab primitives. Difficulty is in
        scissors (✂). Every one has its own page — a 3D model that assembles
        from its parts, a size dial, and a generated cut list.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {COMPOSITES.map((c) => (
          <div
            key={c.id}
            className="flex flex-col gap-2.5 rounded-xl border bg-card/50 p-4 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/50"
          >
            <div className="flex items-center justify-between">
              <span className="text-2xl" aria-hidden>
                {c.emoji}
              </span>
              <span className="text-xs text-primary" title={`difficulty ${c.difficulty}/3`}>
                {"✂".repeat(c.difficulty)}
              </span>
            </div>
            <h3 className="font-semibold">{c.name}</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {c.parts.map((part, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <Link
                    to={labHref(part.shape)}
                    className="shrink-0 rounded-full border border-primary/40 bg-primary/10 px-1.5 text-xs font-medium lowercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                  >
                    {SHAPE_MAP[part.shape].name}
                  </Link>
                  <span>{part.note}</span>
                </li>
              ))}
            </ul>
            <p className="mt-auto border-t pt-2 text-xs leading-relaxed text-muted-foreground">
              {c.plan}
            </p>
            <Link
              to={`/sewing-primitives/make/${c.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              see it in 3D + cut list
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        ))}
      </div>

      {/* The generator: compose, but with dials instead of a recipe. */}
      <Link
        to="/sewing-primitives/creature"
        className="mt-6 flex flex-col gap-2 rounded-xl border border-primary/40 bg-primary/5 p-5 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h3 className="flex items-center gap-2 font-semibold">
            <span className="text-2xl" aria-hidden>
              🪄
            </span>
            Creature generator
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Or skip the menu: dial in chubbiness, limb length and ear size,
            and get a one-of-a-kind plush — lathe body, gored head, cylinder
            limbs — with its full cut list generated live.
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-primary">
          open the generator
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </Link>
    </section>
  </SewingShell>
);

export default SewingMake;
